import React, { useMemo, useState } from 'react';
import { Upload, X, AlertTriangle } from 'lucide-react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import Checkbox from '../ui/Checkbox';
import Badge from '../ui/Badge';
import { useToast } from '../common/ToastContainer';
import basketIoService from '../../services/basketIoService';
import { StrategyMetadata } from '../../types';

interface ExportStrategiesDialogProps {
  isOpen: boolean;
  basketId: string;
  basketName: string;
  strategies: StrategyMetadata[];
  onClose: () => void;
}

/**
 * Picker dialog for the basket-export flow.
 *
 * Lets the user choose any subset of the basket's strategies, then triggers
 * a JSON-bundle download. Marketplace-subscribed baskets never reach this
 * dialog — they're filtered upstream in TabbedBasketManager.
 */
const ExportStrategiesDialog: React.FC<ExportStrategiesDialogProps> = ({
  isOpen,
  basketId,
  basketName,
  strategies,
  onClose,
}) => {
  const { showSuccess, showError } = useToast();
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(strategies.map((s) => s.strategyId)),
  );
  const [exporting, setExporting] = useState(false);

  // Reset selection whenever the dialog re-opens with a different basket.
  React.useEffect(() => {
    if (isOpen) {
      setSelected(new Set(strategies.map((s) => s.strategyId)));
    }
  }, [isOpen, strategies]);

  const allSelected = strategies.length > 0 && selected.size === strategies.length;
  const someSelected = selected.size > 0 && !allSelected;

  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(strategies.map((s) => s.strategyId)));
  };

  const toggleOne = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const orderedSelected = useMemo(
    () => strategies.filter((s) => selected.has(s.strategyId)).map((s) => s.strategyId),
    [strategies, selected],
  );

  const handleExport = async () => {
    if (orderedSelected.length === 0) return;
    try {
      setExporting(true);
      await basketIoService.exportBasket(basketId, orderedSelected);
      showSuccess(
        `Exported ${orderedSelected.length} ${orderedSelected.length === 1 ? 'strategy' : 'strategies'} from "${basketName}"`,
      );
      onClose();
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'Failed to export bundle';
      showError(msg);
    } finally {
      setExporting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={exporting ? () => {} : onClose}
      maxWidth="lg"
      ariaLabel="Export strategies"
      closeOnBackdrop={!exporting}
      closeOnEscape={!exporting}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white inline-flex items-center gap-2">
            <Upload className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
            Export strategies
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 truncate">
            From <span className="font-medium text-gray-700 dark:text-gray-200">{basketName}</span>
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          disabled={exporting}
          aria-label="Close"
          className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
        {strategies.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-center">
            <AlertTriangle className="h-8 w-8 text-amber-500 mb-3" />
            <p className="text-sm font-medium text-gray-900 dark:text-white">No strategies to export</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Add at least one strategy to this basket before exporting.
            </p>
          </div>
        ) : (
          <>
            {/* Select-all bar */}
            <label className="flex items-center justify-between gap-3 px-3 py-2 rounded-md bg-gray-50 dark:bg-gray-700/40 border border-gray-200 dark:border-gray-700 cursor-pointer">
              <span className="inline-flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(el) => { if (el) el.indeterminate = someSelected; }}
                  onChange={toggleAll}
                  className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-emerald-600 focus:ring-2 focus:ring-emerald-500"
                />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                  Select all
                </span>
              </span>
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                {selected.size} of {strategies.length} selected
              </span>
            </label>

            {/* Strategy list */}
            <div className="space-y-2">
              {strategies.map((s) => {
                const checked = selected.has(s.strategyId);
                return (
                  <label
                    key={s.strategyId}
                    className={`block rounded-lg border p-3 cursor-pointer transition-colors ${
                      checked
                        ? 'border-emerald-300 dark:border-emerald-700 bg-emerald-50/50 dark:bg-emerald-900/10'
                        : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/30'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <Checkbox
                        checked={checked}
                        onChange={() => toggleOne(s.strategyId)}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {s.strategyName || 'Unnamed strategy'}
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                          {s.underlying && <Badge variant="info" size="sm">{s.underlying}</Badge>}
                          {s.expiryType && <Badge variant="default" size="sm">{s.expiryType.toUpperCase()}</Badge>}
                          {s.product && <Badge variant="default" size="sm">{s.product}</Badge>}
                          {s.tradingType && <Badge variant="default" size="sm">{s.tradingType}</Badge>}
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {s.legCount} {s.legCount === 1 ? 'leg' : 'legs'}
                            {s.entryTime && s.exitTime && ` · ${s.entryTime}–${s.exitTime}`}
                          </span>
                        </div>
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>

            <p className="text-xs text-gray-500 dark:text-gray-400">
              Bundle excludes broker allocations, positions, and performance metrics.
              Imported strategies start as <span className="font-medium">INACTIVE</span> in the recipient account.
            </p>
          </>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between gap-3 px-6 py-3 border-t border-gray-200 dark:border-gray-700">
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {selected.size > 0
            ? `${selected.size} of ${strategies.length} will be exported`
            : 'Pick at least one strategy'}
        </span>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={exporting}>
            Cancel
          </Button>
          <Button
            size="sm"
            leftIcon={<Upload className="h-4 w-4" />}
            disabled={selected.size === 0 || strategies.length === 0}
            isLoading={exporting}
            onClick={handleExport}
          >
            {exporting ? 'Exporting…' : 'Export'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ExportStrategiesDialog;
